import { useRef } from "react"
import { Dialog } from "@rio-cloud/rio-uikit"
import { useIntl } from "react-intl"

interface OccurrenceImportFileUploadProps {
    show: boolean
    dowloadingTemplate: boolean
    isDragActive: boolean
    selectedFile: File | null
    canImport: boolean
    errorMessage: string | null
    validatingFile: boolean

    onCreateSingleOccurrence: () => void
    onClose: () => void
    handleGetTemplate: () => Promise<void>
    handleDragEnter: React.DragEventHandler<HTMLDivElement>
    handleDragOver: React.DragEventHandler<HTMLDivElement>
    handleDragLeave: React.DragEventHandler<HTMLDivElement>
    handleDrop: React.DragEventHandler<HTMLDivElement>
    handleFileInputChange: React.ChangeEventHandler<HTMLInputElement>
    clearSelectedFile: () => void
    validateFile: () => Promise<void>
}

export const OccurrenceImportFileUpload = (props: OccurrenceImportFileUploadProps) => {
    const intl = useIntl()
    const fileInputRef = useRef<HTMLInputElement>(null)

    return (
        <Dialog
            show={props.show}
            onClose={props.onClose}
            title={
                <div className="font-semibold text-[16px]">
                    {intl.formatMessage({ id: "occurrence.import.create" })}
                </div>
            }
            bsSize="md"
            showCloseButton
            onCloseValidation={() => true}
            body={
                <div>
                    <div className="grid grid-cols-2 md:grid-cols-2 gap-6">
                        <div className="border border-[#d9e1e8] rounded-lg p-8 min-h-[260px] flex flex-col items-center justify-center text-center bg-[#f9fbfc]">
                            <div className="w-[56px] h-[56px] border-2 border-[#2bb3c0] flex items-center justify-center text-[#2bb3c0] mb-4">
                                <span className="rioglyph rioglyph-plus-light text-[40px]" aria-hidden="true" />
                            </div>

                            <h3 className="text-[16px] font-semibold text-[#212529] mb-8 mt-6">
                                {intl.formatMessage({ id: "occurrence.import.createSingle" })}
                            </h3>

                            <button type="button" className="btn btn-primary px-7" onClick={props.onCreateSingleOccurrence}>
                                {intl.formatMessage({ id: "occurrence.import.continue" })}
                            </button>
                        </div>

                        <div
                            className={`relative border rounded-lg p-8 min-h-[260px] flex flex-col items-center justify-center text-center ${props.isDragActive ? "border-[#2bb3c0] bg-[#eefbfc]" : "border-[#d9e1e8] bg-[#f9fbfc]"}`}
                            onDragEnter={props.handleDragEnter}
                            onDragOver={props.handleDragOver}
                            onDragLeave={props.handleDragLeave}
                            onDrop={props.handleDrop}
                        >
                            {props.selectedFile && (
                                <button
                                    type="button"
                                    className="absolute top-3 right-3 btn btn-link p-0 text-color-danger"
                                    onClick={props.clearSelectedFile}
                                    aria-label={intl.formatMessage({ id: "occurrence.import.removeFile" })}
                                    title={intl.formatMessage({ id: "occurrence.import.removeFile" })}
                                >
                                    <span className="rioglyph rioglyph-remove-sign text-[18px]" aria-hidden="true" />
                                </button>
                            )}

                            <div className={`w-[56px] h-[56px] border-2 flex items-center justify-center mb-4 ${props.selectedFile ? "border-[#39b54a] text-[#39b54a]" : "border-[#b8c2cc] text-[#b8c2cc]"}`}>
                                {props.selectedFile ? (
                                    <span className="rioglyph rioglyph-paper-clip text-[30px]" aria-hidden="true" />
                                ) : (
                                    <svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                        <path d="M26 0V3.25H12.675L18.85 9.75H14.3V26H11.05V9.75H6.5L12.675 3.25H0V0H26Z" fill="#697A8B" fillOpacity="0.4" />
                                    </svg>
                                )}
                            </div>

                            <h3 className="text-[16px] font-semibold text-[#212529] mb-6 mt-6">
                                {props.selectedFile
                                    ? intl.formatMessage({ id: "occurrence.import.readyToImport" })
                                    : intl.formatMessage({ id: "occurrence.import.createMultiple" })}
                            </h3>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                className="hidden"
                                onChange={props.handleFileInputChange}
                                tabIndex={-1}
                            />
                            <p
                                className="text-[#6f8599] text-[16px] mb-8 w-full max-w-full px-2 text-center"
                                title={props.selectedFile?.name}
                            >
                                {props.selectedFile ? (
                                    <span className="block overflow-hidden text-ellipsis whitespace-nowrap">
                                        {props.selectedFile.name}
                                    </span>
                                ) : (
                                    <span className="inline-flex flex-wrap items-center justify-center gap-x-1 gap-y-1">
                                        <span>{intl.formatMessage({ id: "occurrence.import.dropXlsxTemplate" })}</span>
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-link p-0 whitespace-normal"
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            {intl.formatMessage({ id: "occurrence.import.selectFile" })}
                                        </button>
                                    </span>
                                )}
                            </p>

                            <div className="flex items-center justify-center gap-8">
                                <button type="button" className="btn btn-default px-7" onClick={props.handleGetTemplate} disabled={props.dowloadingTemplate}>
                                    {intl.formatMessage({ id: props.dowloadingTemplate ? "occurrence.import.downloadingTemplate" : "occurrence.import.downloadTemplate" })}
                                </button>
                                <button type="button" className="btn btn-primary px-7" disabled={!props.canImport || props.validatingFile} onClick={props.validateFile}>
                                    {intl.formatMessage({ id: props.validatingFile ? "occurrence.import.validatingFile" : "occurrence.import.continue" })}
                                </button>
                            </div>
                        </div>
                    </div>

                    {props.errorMessage && 
                        <div className="alert alert-dismissible alert-danger mt-6">
                            <div className="display-flex gap-10">{props.errorMessage}</div>
                        </div>}
                </div>
            }
        />
    )
}